import Parse from 'parse';
import io from 'socket.io-client';

export function connectSymbioCenter(options = {}) {
  const {
    onBpmUpdate = () => {},
    onAniUpdate = () => {},
    onConnectionChange = () => {},
    os = 'Windows',
    filterArtifacts = true // Nouvelle option pour activer/désactiver le filtrage
  } = options;

  let isSocketConnected = false;
  let hasSensorData = false;
  let sensorTimeout = null;

  let lastBPM = 0;
  let lastRR = 0;
  let lastAni = 0;
  let tabBPM = [];
  let tabAni = [];
  let recentBPMs = []; // Tableau pour stocker les dernières valeurs (pour calculer la moyenne)
  let socket = null;

  const socketConfig = {
    resource: "/expert/socket.io",
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    timeout: 20000,
  };

  // Seuils pour la détection des artéfacts
  const BPM_MIN = 30;
  const BPM_MAX = 200;
  const ARTIFACT_THRESHOLD = 30; // Variation maximale acceptable entre mesures consécutives

  function notifyConnectionChange() {
    onConnectionChange({
      isSocketConnected,
      hasSensorData
    });
  }

  function handleSensorData() {
    if (!hasSensorData) {
      hasSensorData = true;
      notifyConnectionChange();
    }
    if (sensorTimeout) clearTimeout(sensorTimeout);
    // Si aucune data n'arrive en 4s, on signale la perte du flux
    sensorTimeout = setTimeout(() => {
      hasSensorData = false;
      notifyConnectionChange();
    }, 4000);
  }

  // Déterminer si une valeur BPM est un artéfact
  function isArtifact(bpmValue) {
    // 1. Vérifier les limites physiologiques
    if (bpmValue < BPM_MIN || bpmValue > BPM_MAX) {
      return true;
    }

    // 2. Si nous n'avons pas assez de données pour la comparaison
    if (recentBPMs.length < 3) {
      return false;
    }

    // 3. Calculer la moyenne mobile des dernières valeurs
    const sum = recentBPMs.reduce((a, b) => a + b, 0);
    const avg = sum / recentBPMs.length;

    // 4. Vérifier si la valeur actuelle s'écarte trop de la moyenne
    return Math.abs(bpmValue - avg) > ARTIFACT_THRESHOLD;
  }

  // Appliquer le filtrage et le lissage
  function processBPM(rawBPM) {
    // Si le filtrage est désactivé, retourner la valeur brute
    if (!filterArtifacts) {
      return rawBPM;
    }

    // Vérifier si c'est un artéfact
    const artifact = isArtifact(rawBPM);

    // Mettre à jour le tableau des dernières valeurs valides
    if (!artifact) {
      // Ajouter la nouvelle valeur et garder les 5 dernières seulement
      recentBPMs.push(rawBPM);
      if (recentBPMs.length > 5) {
        recentBPMs.shift();
      }
    }

    // Si c'est un artéfact, utiliser la dernière valeur valide ou une moyenne
    if (artifact) {
      if (recentBPMs.length > 0) {
        // Utiliser la moyenne des dernières valeurs valides
        const sum = recentBPMs.reduce((a, b) => a + b, 0);
        return Math.round(sum / recentBPMs.length);
      }
      // Si nous n'avons aucune valeur valide, utiliser la valeur brute
      // tout en la limitant à la plage physiologique
      return Math.min(BPM_MAX, Math.max(BPM_MIN, rawBPM));
    }

    // Si ce n'est pas un artéfact, retourner la valeur telle quelle
    return rawBPM;
  }

  function connect() {
    if (typeof io === 'undefined') {
      console.error("Socket.io n'est pas chargé. Assurez-vous d'inclure la bibliothèque socket.io.");
      return;
    }

    socket = io.connect("https://www.symbiocenter.com", socketConfig);

    socket.on("connect", () => {
      isSocketConnected = true;
      notifyConnectionChange();
      try {
        if (Parse && Parse.User && Parse.User.current()) {
          socket.emit("authentication", {
            username: Parse.User.current().attributes.username,
          });
        } else {
          console.error("Parse.User n'est pas disponible ou utilisateur non connecté");
        }
      } catch (e) {
        console.error("Erreur lors de l'authentification:", e);
      }

      socket.on("authenticated", function () {
        console.log("Authentification réussie");
      });

      socket.on("unauthorized", function () {
        console.error("Authentification échouée");
      });

      socket.on("disconnect", function (reason) {
        isSocketConnected = false;
        hasSensorData = false;
        notifyConnectionChange();
        if (sensorTimeout) clearTimeout(sensorTimeout);
        if (reason === "io server disconnect") {
          socket.connect();
        }
      });

      // Réception RR
      socket.on("rr", (rrData) => {
        if (!rrData) return;
        try {
          handleSensorData();
          const rrJson = JSON.parse(rrData);
          lastRR = Math.round(rrJson.rr);
          
          // Calcul du BPM brut
          const rawBPM = os === 'Mac' 
            ? Math.round(60000 / lastRR) 
            : Math.round(60000 / rrJson.rr);
          
          // Traitement du BPM pour filtrer les artéfacts
          lastBPM = processBPM(rawBPM);
          
          // Toujours ajouter au tableau d'historique
          tabBPM.push(lastBPM);
          
          const updateData = {
            bpm: lastBPM,
            rr: Math.round(lastRR),
            cc: Math.round(rrJson.CC || 0),
            pctT1: Math.round(rrJson.PctT1 || 0),
            pctT2: Math.round(rrJson.PctT2 || 0),
            pctT3: Math.round(rrJson.PctT3 || 0),
            traceTacho: Array.isArray(rrJson.traceTacho)
              ? rrJson.traceTacho.map(tacho => ({
                  x: Math.round(tacho.x),
                  y: Math.round(tacho.y)
                }))
              : [],
            isArtifact: isArtifact(rawBPM), // Indiquer si c'est un artéfact
            rawBPM: rawBPM // Inclure la valeur brute pour référence
          };
          
          onBpmUpdate(updateData);
        } catch (error) {
          console.error("Erreur lors du traitement des données RR:", error);
        }
      });

      // Réception ANI (modifiée de la même façon)
      socket.on("ani", (ani) => {
        if (!ani) return;
        try {
          handleSensorData();
          const aniJson = JSON.parse(ani);
          lastAni = Math.round(aniJson.aniValue || 0);
          lastRR = Math.round(aniJson.RR || 0);
          
          // Calcul du BPM brut
          const rawBPM = os === 'Mac' 
            ? Math.round(60000 / lastRR) 
            : Math.round(lastRR);
            
          // Traitement du BPM pour filtrer les artéfacts
          lastBPM = processBPM(rawBPM);
          
          tabBPM.push(lastBPM);
          tabAni.push(lastAni);
          
          const updateData = {
            aniValue: Math.round(lastAni),
            bpm: lastBPM,
            rr: Math.round(lastRR),
            PctT1: Math.round(aniJson.PctT1 || 0),
            PctT2: Math.round(aniJson.PctT2 || 0),
            PctT3: Math.round(aniJson.PctT3 || 0),
            CC: Math.round(aniJson.CC || 0),
            traceTacho: Array.isArray(aniJson.traceTacho)
              ? aniJson.traceTacho.map(tacho => ({
                  x: Math.round(tacho.x),
                  y: Math.round(tacho.y)
                }))
              : [],
            ani: ani,
            isArtifact: isArtifact(rawBPM), // Indiquer si c'est un artéfact
            rawBPM: rawBPM // Inclure la valeur brute pour référence
          };
          
          onAniUpdate(updateData);
        } catch (error) {
          console.error("Erreur lors du traitement des données ANI:", error);
        }
      });

      socket.on("error", (error) => {
        console.error("Erreur socket:", error);
      });
    });
  }

  function disconnect() {
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      isSocketConnected = false;
      hasSensorData = false;
      if (sensorTimeout) clearTimeout(sensorTimeout);
      notifyConnectionChange();
    }
  }

  function resetData() {
    tabBPM = [];
    tabAni = [];
    lastBPM = 0;
    lastRR = 0;
    lastAni = 0;
    recentBPMs = [];
  }

  return {
    connect,
    disconnect,
    resetData,
    getStatus: () => ({
      isSocketConnected,
      hasSensorData,
      lastBPM: Math.round(lastBPM),
      lastRR: Math.round(lastRR),
      lastAni: Math.round(lastAni)
    }),
    getData: () => ({
      tabBPM: tabBPM.map(val => Math.round(val)),
      tabAni: tabAni.map(val => Math.round(val))
    }),
    sendMessage: (event, data) => {
      if (socket && hasSensorData) {
        socket.emit(event, data);
      } else {
        console.warn("Impossible d'envoyer un message: non connecté");
      }
    },
    // Nouvelle méthode pour activer/désactiver le filtrage
    setFilterArtifacts: (enabled) => {
      filterArtifacts = enabled;
    }
  };
}